import { Col, Row } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import { get, set } from "lodash";
import { useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  MdOutlineCalendarToday,
  MdOutlineEmail,
  MdOutlineFrontHand,
  MdOutlinePinDrop,
  MdOutlineSouthAmerica,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import useZipCodeCities from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import { TERMINATION_RECIPIENTS } from "utils/api/graphql/queries/termination";
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
  MILTIS_PRODUCT_CODES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import { filterContractNumber } from "utils/helpers/validations";

const TerminationRequest = ({
  initialValues,
  setPercent,
  bindField,
  setDisabled,
  subscription,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const isLumineis =
    get(subscription, "insurancePlan.product") ===
    MILTIS_PRODUCT_CODES.LUMINEIS;
  const LUMINEIS_FIELDS = SUBSCRIPTION.FORM.EXISTING_CONTRACT?.LUMINEIS || {};
  const [terminationZipCode, setTerminationZipCode] = useState(() =>
    get(initialValues, "project.terminationRequest.zipCode"),
  );
  const { cities, loading: citiesLoading } =
    useZipCodeCities(terminationZipCode);

  const contractMoreThanOneYear = get(
    initialValues,
    "project.fields.contractMoreThanOneYear",
    false,
  );
  const selfOccupation = get(
    initialValues,
    "project.fields.selfOccupation",
    undefined,
  );

  const handleHasExistingContractChange = (checked) => {
    set(initialValues, "project.ria", checked);
    set(initialValues, "project.fields.contractMoreThanOneYear", false);
    set(initialValues, "project.fields.selfOccupation", undefined);
    set(initialValues, "project.terminationRequest", null);
    setPercent(initialValues);
  };

  const handleContractMoreThanOneYearChange = (checked) => {
    set(initialValues, "project.fields.contractMoreThanOneYear", checked);
    set(initialValues, "project.fields.selfOccupation", undefined);
    set(initialValues, "project.terminationRequest", null);
    setPercent(initialValues);
  };

  const handleWantsMiltisToHandleChange = (checked) => {
    set(initialValues, "project.fields.selfOccupation", !checked);
    if (checked) {
      set(initialValues, "project.terminationRequest", {
        id: cuid(),
        reason: "22",
        brokerageMandate: { id: cuid() },
      });
      flushSync(() => {
        setDisabled((prev) => {
          setPercent(initialValues, {
            ...prev,
            project: {
              ...prev.project,
              terminationRequest: undefined,
            },
          });
          return {
            ...prev,
            project: {
              ...prev.project,
              terminationRequest: undefined,
            },
          };
        });
      });
    } else {
      set(initialValues, "project.terminationRequest", null);
      flushSync(() => {
        setDisabled((prev) => {
          const terminationRequestDisabled = isLumineis
            ? {
                contractNumber: true,
                address: true,
                zipCode: true,
                city: true,
                email: true,
                brokerageMandate: { currentCompany: true },
              }
            : {
                recipient: true,
                dueDate: true,
                contractNumber: true,
              };
          setPercent(initialValues, {
            ...prev,
            project: {
              ...prev.project,
              terminationRequest: terminationRequestDisabled,
            },
          });
          return {
            ...prev,
            project: {
              ...prev.project,
              terminationRequest: terminationRequestDisabled,
            },
          };
        });
      });
    }
  };

  const buildSwitchField = (path, label, onChangeExtra) => {
    const field = bindField(path, {
      label,
      onChange: (value) => {
        if (typeof onChangeExtra === "function") {
          onChangeExtra(value);
        } else {
          setPercent(initialValues);
        }
      },
    });
    return (
      <FormField
        path={field.path}
        label={field.label}
        required={field.required}
        disabled={field.disabled}
      >
        {(inputField) => (
          <Input
            type="switch"
            label={label}
            checked={get(initialValues, path) === true}
            {...inputField}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      </FormField>
    );
  };

  const contractNumberPath = "project.terminationRequest.contractNumber";
  const contractNumberField = bindField(contractNumberPath, {
    label:
      LUMINEIS_FIELDS.CONTRACT_NUMBER?.LABEL ||
      SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CONTRACT_NUMBER?.LABEL ||
      "N° de contrat",
    required: true,
    extractValue: ({ target: { value } }) => filterContractNumber(value),
    onBlur: () => setPercent(initialValues),
  });

  const currentCompanyPath =
    "project.terminationRequest.brokerageMandate.currentCompany";
  const currentCompanyField = bindField(currentCompanyPath, {
    label:
      LUMINEIS_FIELDS.CURRENT_COMPANY?.LABEL ||
      SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CURRENT_COMPANY?.LABEL ||
      "Assuré par",
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  const addressPath = "project.terminationRequest.address";
  const addressField = bindField(addressPath, {
    label:
      LUMINEIS_FIELDS.ADDRESS?.LABEL ||
      "Adresse postale de l'assureur actuel",
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  const zipCodePath = "project.terminationRequest.zipCode";
  const zipCodeField = bindField(zipCodePath, {
    label: LUMINEIS_FIELDS.ZIP_CODE?.LABEL || "Code postal",
    required: true,
    extractValue: ({ target: { value } }) =>
      value.replace(/\D/g, "").slice(0, 5),
    onChange: (zipCode) => {
      set(initialValues, "project.terminationRequest.city", "");
      setTerminationZipCode(zipCode);
    },
    onBlur: () => setPercent(initialValues),
  });

  const cityPath = "project.terminationRequest.city";
  const cityField = bindField(cityPath, {
    label: LUMINEIS_FIELDS.CITY?.LABEL || "Ville",
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  const emailPath = "project.terminationRequest.email";
  const emailField = bindField(emailPath, {
    label:
      LUMINEIS_FIELDS.EMAIL?.LABEL ||
      "Adresse email de contact de l'assureur",
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  const recipientPath = "project.terminationRequest.recipient";
  const recipientField = bindField(recipientPath, {
    label:
      SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CURRENT_COMPANY?.LABEL ||
      "Nom de l'assureur actuel du prospect",
    required: true,
    onChange: (value, eventOrValue) => {
      const option =
        eventOrValue && typeof eventOrValue === "object" && "name" in eventOrValue
          ? eventOrValue
          : eventOrValue?.option;
      set(
        initialValues,
        "project.terminationRequest.brokerageMandate.currentCompany",
        option?.name || value || "",
      );
      setPercent(initialValues);
    },
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST}-section`}
    >
      <Form
        type="vertical"
        title={
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.TITLE
        }
        subtitle={
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.SUBTITLE
        }
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            {buildSwitchField(
              "project.ria",
              SUBSCRIPTION.FORM.EXISTING_CONTRACT?.HAS_EXISTING_CONTRACT ||
                "Le prospect dispose-t-il actuellement d'un contrat de complémentaire santé ?",
              handleHasExistingContractChange,
            )}
          </Col>
          {get(initialValues, "project.ria", false) && (
            <Col lg={24} xs={24}>
              {buildSwitchField(
                "project.fields.contractMoreThanOneYear",
                SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CONTRACT_MORE_THAN_YEAR ||
                  "Contrat du prospect souscrit depuis plus d'un an",
                handleContractMoreThanOneYearChange,
              )}
            </Col>
          )}
          {get(initialValues, "project.ria", false) &&
            contractMoreThanOneYear && (
              <Col lg={24} xs={24}>
                <FormField
                  path="project.fields.selfOccupation"
                  label={
                    SUBSCRIPTION.FORM.EXISTING_CONTRACT
                      ?.WANTS_MILTIS_TO_HANDLE ||
                    "L'adhérent souhaite que Miltis s'occupe de la résiliation"
                  }
                >
                  {(inputField) => (
                    <Input
                      type="switch"
                      label={
                        SUBSCRIPTION.FORM.EXISTING_CONTRACT
                          ?.WANTS_MILTIS_TO_HANDLE ||
                        "L'adhérent souhaite que Miltis s'occupe de la résiliation"
                      }
                      checked={selfOccupation === false}
                      {...inputField}
                      onChange={handleWantsMiltisToHandleChange}
                    />
                  )}
                </FormField>
              </Col>
            )}
          {get(initialValues, "project.ria", false) &&
            contractMoreThanOneYear &&
            selfOccupation === false &&
            (isLumineis ? (
              <>
                <Col lg={24} xs={24}>
                  <FormField
                    path={contractNumberField.path}
                    label={contractNumberField.label}
                    required={contractNumberField.required}
                  >
                    {(field) => (
                      <Input
                        label={contractNumberField.label}
                        labelSuffix={
                          LUMINEIS_FIELDS.CONTRACT_NUMBER?.SUBLABEL ||
                          "(référence de votre contrat)"
                        }
                        placeholder={
                          LUMINEIS_FIELDS.CONTRACT_NUMBER?.PLACEHOLDER ||
                          "ex : 123456789"
                        }
                        icon={<MdOutlineFrontHand size={16} />}
                        type="custom"
                        stripSpaces={true}
                        defaultValue={contractNumberField.defaultValue}
                        {...field}
                        onChange={contractNumberField.onChange}
                        onBlur={contractNumberField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={currentCompanyField.path}
                    label={currentCompanyField.label}
                    required={currentCompanyField.required}
                  >
                    {(field) => (
                      <Input
                        label={currentCompanyField.label}
                        labelSuffix={
                          LUMINEIS_FIELDS.CURRENT_COMPANY?.SUBLABEL ||
                          "(nom de l'assureur actuel)"
                        }
                        placeholder={
                          LUMINEIS_FIELDS.CURRENT_COMPANY?.PLACEHOLDER ||
                          "ex : ABC Mutuelle"
                        }
                        type="custom"
                        normalizeText={true}
                        defaultValue={currentCompanyField.defaultValue}
                        {...field}
                        onChange={currentCompanyField.onChange}
                        onBlur={currentCompanyField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={addressField.path}
                    label={addressField.label}
                    required={addressField.required}
                  >
                    {(field) => (
                      <Input
                        label={addressField.label}
                        placeholder={
                          LUMINEIS_FIELDS.ADDRESS?.PLACEHOLDER ||
                          "ex : 10 Rue de la Liberté"
                        }
                        icon={<MdOutlinePinDrop size={16} />}
                        type="custom"
                        normalizeText={true}
                        defaultValue={addressField.defaultValue}
                        {...field}
                        onChange={addressField.onChange}
                        onBlur={addressField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={12} xs={24}>
                  <FormField
                    path={zipCodeField.path}
                    label={zipCodeField.label}
                    required={zipCodeField.required}
                  >
                    {(field) => (
                      <Input
                        label={zipCodeField.label}
                        placeholder={
                          LUMINEIS_FIELDS.ZIP_CODE?.PLACEHOLDER || "ex : 69003"
                        }
                        defaultValue={zipCodeField.defaultValue}
                        {...field}
                        onChange={zipCodeField.onChange}
                        onBlur={zipCodeField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={12} xs={24}>
                  <FormField
                    path={cityField.path}
                    label={cityField.label}
                    required={cityField.required}
                  >
                    {(field) => (
                      <Select
                        icon={<MdOutlineSouthAmerica size={16} />}
                        label={cityField.label}
                        placeholder={
                          LUMINEIS_FIELDS.CITY?.PLACEHOLDER || "Sélectionnez"
                        }
                        showSearch
                        loading={citiesLoading}
                        options={cities}
                        defaultValue={cityField.defaultValue}
                        {...field}
                        onChange={cityField.onChange}
                        onBlur={cityField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={emailField.path}
                    label={emailField.label}
                    required={emailField.required}
                  >
                    {(field) => (
                      <Input
                        label={emailField.label}
                        placeholder={
                          LUMINEIS_FIELDS.EMAIL?.PLACEHOLDER ||
                          "ex : contact@assureur.fr"
                        }
                        type="email"
                        icon={<MdOutlineEmail size={16} />}
                        defaultValue={emailField.defaultValue}
                        {...field}
                        onChange={emailField.onChange}
                        onBlur={emailField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </>
            ) : (
              <>
                <Col lg={24} xs={24}>
                  <Input
                    label={
                      SUBSCRIPTION.FORM.EXISTING_CONTRACT
                        ?.CURRENT_EFFECTIVE_DATE?.LABEL ||
                      "Date d'effet du contrat actuel du prospect"
                    }
                    placeholder={
                      SUBSCRIPTION.FORM.EXISTING_CONTRACT
                        ?.CURRENT_EFFECTIVE_DATE?.PLACEHOLDER || "JJ/MM/AAAA"
                    }
                    type="date"
                    variant="simple"
                    format={DATE_FORMAT}
                    disabledDate={(current) =>
                      current && current > dayjs().subtract(1, "year")
                    }
                    defaultPickerValue={dayjs().subtract(1, "year")}
                    defaultValue={(() => {
                      const dateValue = get(
                        initialValues,
                        "project.terminationRequest.dueDate",
                      );
                      if (!dateValue) return undefined;
                      const parsedDate = dayjs(
                        dateValue,
                        API_DATE_FORMAT,
                        true,
                      ).isValid()
                        ? dayjs(dateValue, API_DATE_FORMAT, true)
                        : dayjs(dateValue);
                      return parsedDate.isValid() ? parsedDate : undefined;
                    })()}
                    icon={<MdOutlineCalendarToday size={16} />}
                    onChange={(date) => {
                      set(
                        initialValues,
                        "project.terminationRequest.dueDate",
                        date
                          ? date
                              .set("hour", 4)
                              .set("minute", 0)
                              .set("second", 0)
                          : date,
                      );
                    }}
                    onBlur={() => setPercent(initialValues)}
                    required
                  />
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={contractNumberField.path}
                    label={contractNumberField.label}
                    required={contractNumberField.required}
                  >
                    {(field) => (
                      <Input
                        label={contractNumberField.label}
                        placeholder={
                          SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CONTRACT_NUMBER
                            ?.PLACEHOLDER || "ex : 123456789"
                        }
                        icon={<MdOutlineFrontHand size={16} />}
                        type="custom"
                        stripSpaces={true}
                        defaultValue={contractNumberField.defaultValue}
                        {...field}
                        onChange={contractNumberField.onChange}
                        onBlur={contractNumberField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={recipientField.path}
                    label={recipientField.label}
                    required={recipientField.required}
                  >
                    {(field) => (
                      <Select
                        type="async"
                        node={{
                          query: TERMINATION_RECIPIENTS,
                          variables: {},
                          accessors: {
                            root: "terminationRecipients.data",
                            label: "name",
                            value: "id",
                          },
                        }}
                        label={recipientField.label}
                        placeholder={
                          SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CURRENT_COMPANY
                            ?.PLACEHOLDER || "Sélectionner une compagnie"
                        }
                        defaultValue={recipientField.defaultValue}
                        {...field}
                        onChange={recipientField.onChange}
                      />
                    )}
                  </FormField>
                </Col>
              </>
            ))}
        </Row>
      </Form>
    </div>
  );
};

export default TerminationRequest;
